import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, UserPlus, Pencil, Trash2, KeyRound, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ManageableOrg {
  id: number;
  client_alias: string;
  user_role?: string;
}

type Scope = 'all' | 'none' | 'selected';

interface Target { id: number; name: string }

interface Member {
  user_id: number;
  name: string;
  email: string;
  is_active: boolean;
  role: 'owner' | 'manager' | 'user';
  practice_scope: Scope;
  aco_scope: Scope;
  practices: Target[];
  acos: Target[];
}

interface PendingInvite {
  id: number;
  invitee_email: string;
  role: 'manager' | 'user';
  practice_scope: Scope;
  aco_scope: Scope;
  practice_ids: number[];
  aco_ids: number[];
  expires_at: string;
  inviter_name: string;
}

interface ScopeState {
  practiceScope: Scope;
  practiceIds: number[];
  acoScope: Scope;
  acoIds: number[];
}

const DEFAULT_SCOPE: ScopeState = { practiceScope: 'all', practiceIds: [], acoScope: 'all', acoIds: [] };

function scopeSummary(practiceScope: Scope, acoScope: Scope, practices: Target[], acos: Target[], hasAcos: boolean): string {
  const parts: string[] = [];
  if (practiceScope === 'all') parts.push('All practices');
  else if (practiceScope === 'none') parts.push('No practices');
  else parts.push(`${practices.length} practice${practices.length === 1 ? '' : 's'}`);
  if (hasAcos) {
    if (acoScope === 'all') parts.push('All ACOs');
    else if (acoScope === 'none') parts.push('No ACOs');
    else parts.push(`${acos.length} ACO${acos.length === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
}

/** Scope picker: All / None / Selected + multi-select checkboxes. */
function ScopePicker({
  label, scope, ids, targets, onChange,
}: {
  label: string;
  scope: Scope;
  ids: number[];
  targets: Target[];
  onChange: (scope: Scope, ids: number[]) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <RadioGroup
        value={scope}
        onValueChange={(v) => onChange(v as Scope, v === 'selected' ? ids : [])}
        className="flex gap-4"
      >
        <div className="flex items-center gap-1.5">
          <RadioGroupItem value="all" id={`${label}-all`} />
          <Label htmlFor={`${label}-all`} className="font-normal">All</Label>
        </div>
        <div className="flex items-center gap-1.5">
          <RadioGroupItem value="none" id={`${label}-none`} />
          <Label htmlFor={`${label}-none`} className="font-normal">None</Label>
        </div>
        <div className="flex items-center gap-1.5">
          <RadioGroupItem value="selected" id={`${label}-selected`} />
          <Label htmlFor={`${label}-selected`} className="font-normal">Selected…</Label>
        </div>
      </RadioGroup>
      {scope === 'selected' && (
        <div className="max-h-36 space-y-1.5 overflow-y-auto rounded-md border p-2">
          {targets.length === 0 && (
            <p className="text-sm text-muted-foreground">Nothing available to select.</p>
          )}
          {targets.map((t) => (
            <div key={t.id} className="flex items-center gap-2">
              <Checkbox
                id={`${label}-${t.id}`}
                checked={ids.includes(t.id)}
                onCheckedChange={(checked) =>
                  onChange('selected', checked ? [...ids, t.id] : ids.filter((i) => i !== t.id))
                }
              />
              <Label htmlFor={`${label}-${t.id}`} className="font-normal">{t.name}</Label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Clean dropdown multi-select for organizations (search + checkboxes + Select all).
 * Closed state matches a normal select; open state stays multi-select.
 */
function OrgMultiSelectDropdown({
  orgs,
  selectedIds,
  onChange,
  roleLabel,
}: {
  orgs: ManageableOrg[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  roleLabel: (org: ManageableOrg) => string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => o.client_alias.toLowerCase().includes(q));
  }, [orgs, query]);

  const allSelected = orgs.length > 0 && selectedIds.length === orgs.length;
  const someSelected = selectedIds.length > 0 && !allSelected;
  const selectedLabel = useMemo(() => {
    if (selectedIds.length === 0) return null;
    if (selectedIds.length === orgs.length) return `All ${orgs.length} organizations`;
    if (selectedIds.length === 1) {
      return orgs.find((o) => o.id === selectedIds[0])?.client_alias ?? '1 organization';
    }
    return `${selectedIds.length} of ${orgs.length} organizations`;
  }, [selectedIds, orgs]);

  const toggle = (id: number, checked: boolean) => {
    onChange(checked ? [...selectedIds, id] : selectedIds.filter((i) => i !== id));
  };

  return (
    <div className="space-y-2">
      <Label>Organizations</Label>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery('');
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-auto w-full justify-between px-3 py-2 font-normal"
          >
            {selectedLabel ? (
              <span className="min-w-0 text-left">
                <span className="block truncate font-medium">{selectedLabel}</span>
                {selectedIds.length === 1 && (() => {
                  const only = orgs.find((o) => o.id === selectedIds[0]);
                  return only ? (
                    <span className="block text-xs text-muted-foreground">{roleLabel(only)}</span>
                  ) : null;
                })()}
                {selectedIds.length > 1 && (
                  <span className="block text-xs text-muted-foreground">
                    Open to change selection
                  </span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground">Select organizations…</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="border-b p-2">
            <Input
              placeholder="Search organizations…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-3 py-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="org-select-all-header"
                className="h-[18px] w-[18px] rounded-[3px]"
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={(v) => onChange(v ? orgs.map((o) => o.id) : [])}
              />
              <Label htmlFor="org-select-all-header" className="cursor-pointer text-sm font-medium">
                Select all
              </Label>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onChange([])}
              disabled={selectedIds.length === 0}
            >
              Clear
            </Button>
          </div>
          <div className="max-h-64 space-y-0.5 overflow-y-auto p-1">
            {filtered.length === 0 && (
              <p className="px-2 py-3 text-sm text-muted-foreground">No matches.</p>
            )}
            {filtered.map((org) => {
              const checked = selectedIds.includes(org.id);
              return (
                <div
                  key={org.id}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-2 py-2 hover:bg-accent/40',
                    checked && 'bg-accent/30'
                  )}
                >
                  <Checkbox
                    id={`org-check-${org.id}`}
                    className="h-[18px] w-[18px] rounded-[3px]"
                    checked={checked}
                    onCheckedChange={(v) => toggle(org.id, !!v)}
                  />
                  <Label
                    htmlFor={`org-check-${org.id}`}
                    className="min-w-0 flex-1 cursor-pointer font-normal"
                  >
                    <span className="block truncate font-medium">{org.client_alias}</span>
                    <span className="block text-xs text-muted-foreground">{roleLabel(org)}</span>
                  </Label>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Team management for ONE organization — members, invites, scope, transfer.
 * Invite targets come from the sheet-level org checkbox selection.
 */
function TeamManagement({
  clientId,
  isOwner,
  inviteClientIds,
  inviteOrgLabels,
}: {
  clientId: number;
  isOwner: boolean;
  /** Orgs checked in the sheet-level picker — who the invite goes to. */
  inviteClientIds: number[];
  inviteOrgLabels: string[];
}) {
  const { refreshClients } = useAuth();
  const { toast } = useToast();

  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [clientPractices, setClientPractices] = useState<Target[]>([]);
  const [clientAcos, setClientAcos] = useState<Target[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'manager' | 'user'>('user');
  const [inviteScope, setInviteScope] = useState<ScopeState>(DEFAULT_SCOPE);
  const multiOrgInvite = inviteClientIds.length > 1;

  const [editing, setEditing] = useState<Member | null>(null);
  const [editRole, setEditRole] = useState<'manager' | 'user'>('user');
  const [editScope, setEditScope] = useState<ScopeState>(DEFAULT_SCOPE);

  const [removing, setRemoving] = useState<Member | null>(null);

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState<number | null>(null);
  const [transferPassword, setTransferPassword] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [membersData, invitesData] = await Promise.all([
        apiClient.get(`/api/clients/${clientId}/members`),
        apiClient.get(`/api/clients/${clientId}/invites`),
      ]);
      setMembers(membersData.members || []);
      setClientPractices(membersData.clientPractices || []);
      setClientAcos(membersData.clientAcos || []);
      setInvites(invitesData.invites || []);
    } catch (error) {
      console.error('Failed to load team:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const hasAcos = clientAcos.length > 0;

  const resetInviteForm = () => {
    setInviteOpen(false);
    setInviteEmail('');
    setInviteRole('user');
    setInviteScope(DEFAULT_SCOPE);
  };

  const openInviteForm = () => {
    if (inviteClientIds.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Select at least one organization',
        description: 'Check the organizations above that you want to share.',
      });
      return;
    }
    setInviteEmail('');
    setInviteRole('user');
    setInviteScope(DEFAULT_SCOPE);
    setInviteOpen(true);
  };

  const handleInvite = async () => {
    if (inviteClientIds.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Select at least one organization',
        description: 'Check the organizations above that you want to share.',
      });
      return;
    }
    setBusy(true);
    try {
      let programWarning = false;
      if (multiOrgInvite || inviteClientIds[0] !== clientId) {
        // Multi-org (or a different single org): batch API — all practices / all ACOs per org.
        const result = await apiClient.post('/api/invites/batch', {
          email: inviteEmail.trim(),
          role: inviteRole,
          clientIds: inviteClientIds,
        });
        programWarning = !!result.programWarning;
      } else {
        // Single current org — keep custom practice/ACO scope pickers.
        const result = await apiClient.post(`/api/clients/${clientId}/invites`, {
          email: inviteEmail.trim(),
          role: inviteRole,
          practiceScope: inviteScope.practiceScope,
          practiceIds: inviteScope.practiceScope === 'selected' ? inviteScope.practiceIds : [],
          acoScope: hasAcos ? inviteScope.acoScope : 'none',
          acoIds: hasAcos && inviteScope.acoScope === 'selected' ? inviteScope.acoIds : [],
        });
        programWarning = !!result.programWarning;
      }
      const count = inviteClientIds.length;
      toast({
        title: count > 1 ? `Invitations sent (${count})` : 'Invitation sent',
        description: programWarning
          ? 'Note: this person does not have APP Plus enabled — an administrator must enable it before they can see ACOs.'
          : 'No email is sent — they will see it under Pending invitations when they sign in.',
      });
      resetInviteForm();
      await load();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not send invitation',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (member: Member) => {
    setEditing(member);
    setEditRole(member.role === 'manager' ? 'manager' : 'user');
    setEditScope({
      practiceScope: member.practice_scope,
      practiceIds: member.practices.map((p) => p.id),
      acoScope: member.aco_scope,
      acoIds: member.acos.map((a) => a.id),
    });
  };

  const handleEditSave = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await apiClient.patch(`/api/clients/${clientId}/members/${editing.user_id}`, {
        role: editRole,
        practiceScope: editScope.practiceScope,
        practiceIds: editScope.practiceScope === 'selected' ? editScope.practiceIds : [],
        acoScope: hasAcos ? editScope.acoScope : 'none',
        acoIds: hasAcos && editScope.acoScope === 'selected' ? editScope.acoIds : [],
      });
      toast({ title: 'Member updated' });
      setEditing(null);
      await load();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not update member',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await apiClient.delete(`/api/clients/${clientId}/members/${removing.user_id}`);
      toast({ title: 'Member removed', description: `${removing.name} no longer has access.` });
      setRemoving(null);
      await load();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not remove member',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRevokeInvite = async (invite: PendingInvite) => {
    try {
      await apiClient.delete(`/api/clients/${clientId}/invites/${invite.id}`);
      toast({ title: 'Invitation revoked' });
      await load();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not revoke invitation',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const handleTransfer = async () => {
    if (!transferTarget) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/clients/${clientId}/ownership/transfer`, {
        targetUserId: transferTarget,
        password: transferPassword,
      });
      toast({ title: 'Ownership transferred' });
      setTransferOpen(false);
      setTransferPassword('');
      setTransferTarget(null);
      await Promise.all([load(), refreshClients()]);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not transfer ownership',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  const transferCandidates = members.filter((m) => m.role !== 'owner' && m.is_active);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {!inviteOpen && (
          <div className="flex justify-end">
            <Button onClick={openInviteForm}>
              <UserPlus className="mr-2 h-4 w-4" />
              Invite collaborator
            </Button>
          </div>
        )}

        {/* Inline invite form — the sheet is the surface; no nested modal. */}
        {inviteOpen && (
          <div className="rounded-lg border bg-card shadow-sm">
            <div className="border-b bg-muted/40 px-5 py-3">
              <h4 className="flex items-center gap-2 font-semibold">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                  <UserPlus className="h-4 w-4 text-primary" />
                </span>
                Invite a collaborator
              </h4>
            </div>
            <div className="space-y-5 px-5 py-4">
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                <span className="font-medium">
                  Sharing {inviteClientIds.length} organization{inviteClientIds.length === 1 ? '' : 's'}
                </span>
                {multiOrgInvite ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Each gets all practices and all ACOs (when available). Open Organizations above to change.
                  </p>
                ) : inviteOrgLabels[0] ? (
                  <p className="mt-1 text-xs text-muted-foreground">{inviteOrgLabels[0]}</p>
                ) : null}
                {inviteOrgLabels.length > 1 && inviteOrgLabels.length <= 6 && (
                  <ul className="mt-1 list-inside list-disc text-xs text-muted-foreground">
                    {inviteOrgLabels.map((label) => (
                      <li key={label}>{label}</li>
                    ))}
                  </ul>
                )}
                {inviteOrgLabels.length > 6 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {inviteOrgLabels.slice(0, 4).join(', ')} and {inviteOrgLabels.length - 4} more
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="invite-email">Email address</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="name@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  Must be an existing account. No email is sent — the invitation shows under
                  Pending invitations when they sign in, and expires in 14 days.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Role">
                  {([
                    { value: 'user' as const, name: 'User', blurb: 'Works data on the shared practices and ACOs' },
                    { value: 'manager' as const, name: 'Manager', blurb: 'Also invites and manages team members' },
                  ]).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={inviteRole === opt.value}
                      onClick={() => setInviteRole(opt.value)}
                      className={`rounded-md border p-3 text-left transition-colors ${
                        inviteRole === opt.value
                          ? 'border-primary bg-accent/40 ring-1 ring-primary'
                          : 'border-border hover:bg-accent/20'
                      }`}
                    >
                      <span className="block text-sm font-medium">{opt.name}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{opt.blurb}</span>
                    </button>
                  ))}
                </div>
              </div>

              {!multiOrgInvite && inviteClientIds.length === 1 && inviteClientIds[0] === clientId && (
                <div className={`grid grid-cols-1 gap-4 ${hasAcos ? 'sm:grid-cols-2' : ''}`}>
                  <ScopePicker
                    label="Practices"
                    scope={inviteScope.practiceScope}
                    ids={inviteScope.practiceIds}
                    targets={clientPractices}
                    onChange={(scope, ids) => setInviteScope((s) => ({ ...s, practiceScope: scope, practiceIds: ids }))}
                  />
                  {hasAcos && (
                    <ScopePicker
                      label="ACOs (APP Plus)"
                      scope={inviteScope.acoScope}
                      ids={inviteScope.acoIds}
                      targets={clientAcos}
                      onChange={(scope, ids) => setInviteScope((s) => ({ ...s, acoScope: scope, acoIds: ids }))}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t bg-muted/40 px-5 py-3">
              <Button variant="ghost" onClick={resetInviteForm} disabled={busy}>Cancel</Button>
              <Button
                onClick={handleInvite}
                disabled={busy || !inviteEmail.trim() || inviteClientIds.length === 0
                  || (!multiOrgInvite && inviteClientIds[0] === clientId && (
                    (inviteScope.practiceScope === 'selected' && inviteScope.practiceIds.length === 0)
                    || (hasAcos && inviteScope.acoScope === 'selected' && inviteScope.acoIds.length === 0)
                    || (inviteScope.practiceScope === 'none' && (!hasAcos || inviteScope.acoScope === 'none'))
                  ))}
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {inviteClientIds.length > 1
                  ? `Send ${inviteClientIds.length} invitations`
                  : 'Send invitation'}
              </Button>
            </div>
          </div>
        )}

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Access</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.user_id}>
                  <TableCell>
                    <div className="font-medium">
                      {member.name}
                      {!member.is_active && (
                        <Badge variant="outline" className="ml-2">deactivated</Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">{member.email}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
                      {member.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {member.role === 'owner'
                      ? 'Everything'
                      : scopeSummary(member.practice_scope, member.aco_scope, member.practices, member.acos, hasAcos)}
                  </TableCell>
                  <TableCell className="text-right">
                    {member.role !== 'owner' && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(member)} title="Edit role & access">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setRemoving(member)} title="Remove from organization">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {invites.length > 0 && (
          <div>
            <h4 className="mb-2 text-sm font-medium">Pending invitations</h4>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((invite) => (
                    <TableRow key={invite.id}>
                      <TableCell>{invite.invitee_email}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{invite.role}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(invite.expires_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleRevokeInvite(invite)}>
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {isOwner && transferCandidates.length > 0 && (
          <div className="rounded-md border border-destructive/30 p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h4 className="flex items-center gap-2 text-sm font-medium">
                  <KeyRound className="h-4 w-4" />
                  Transfer ownership
                </h4>
                <p className="text-sm text-muted-foreground">
                  Make another member the owner. You will become a manager. Requires your password.
                </p>
              </div>
              <Button variant="outline" onClick={() => setTransferOpen(true)}>
                Transfer…
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Edit member dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit {editing?.name}</DialogTitle>
            <DialogDescription>Change this member's role and access scope.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Role</Label>
              <RadioGroup value={editRole} onValueChange={(v) => setEditRole(v as 'manager' | 'user')} className="flex gap-4">
                <div className="flex items-center gap-1.5">
                  <RadioGroupItem value="user" id="edit-role-user" />
                  <Label htmlFor="edit-role-user" className="font-normal">User</Label>
                </div>
                <div className="flex items-center gap-1.5">
                  <RadioGroupItem value="manager" id="edit-role-manager" />
                  <Label htmlFor="edit-role-manager" className="font-normal">Manager</Label>
                </div>
              </RadioGroup>
            </div>
            <ScopePicker
              label="Practices"
              scope={editScope.practiceScope}
              ids={editScope.practiceIds}
              targets={clientPractices}
              onChange={(scope, ids) => setEditScope((s) => ({ ...s, practiceScope: scope, practiceIds: ids }))}
            />
            {hasAcos && (
              <ScopePicker
                label="ACOs (APP Plus)"
                scope={editScope.acoScope}
                ids={editScope.acoIds}
                targets={clientAcos}
                onChange={(scope, ids) => setEditScope((s) => ({ ...s, acoScope: scope, acoIds: ids }))}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button
              onClick={handleEditSave}
              disabled={busy
                || (editScope.practiceScope === 'selected' && editScope.practiceIds.length === 0)
                || (hasAcos && editScope.acoScope === 'selected' && editScope.acoIds.length === 0)}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirm */}
      <Dialog open={!!removing} onOpenChange={(open) => { if (!open) setRemoving(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove {removing?.name}?</DialogTitle>
            <DialogDescription>
              They will immediately lose access to this organization and all of its
              practices and ACOs. Their account is not deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoving(null)} disabled={busy}>Cancel</Button>
            <Button variant="destructive" onClick={handleRemove} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remove member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ownership transfer dialog */}
      <Dialog open={transferOpen} onOpenChange={(open) => { if (!open) { setTransferOpen(false); setTransferPassword(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer ownership</DialogTitle>
            <DialogDescription>
              The new owner gains full control of this organization; you become a manager.
              This cannot be undone without the new owner's consent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>New owner</Label>
              <RadioGroup
                value={transferTarget?.toString() ?? ''}
                onValueChange={(v) => setTransferTarget(parseInt(v, 10))}
                className="gap-2"
              >
                {transferCandidates.map((m) => (
                  <div key={m.user_id} className="flex items-center gap-2">
                    <RadioGroupItem value={m.user_id.toString()} id={`transfer-${m.user_id}`} />
                    <Label htmlFor={`transfer-${m.user_id}`} className="font-normal">
                      {m.name} <span className="text-muted-foreground">({m.email})</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-2">
              <Label htmlFor="transfer-password">Confirm with your password</Label>
              <Input
                id="transfer-password"
                type="password"
                value={transferPassword}
                onChange={(e) => setTransferPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleTransfer}
              disabled={busy || !transferTarget || !transferPassword}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Transfer ownership
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Team & Access — top-level sheet. Checkbox multi-select (with Select all)
 * picks which orgs to share; members/transfer show for the first checked org.
 * Multi-org invites grant all practices / all ACOs per org.
 */
export function TeamAccessSheet({
  open,
  onOpenChange,
  initialClientId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialClientId?: number;
}) {
  const { clients, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const manageable = clients.filter(
    (c) => c.user_role === 'owner' || c.user_role === 'manager' || isAdmin
  );

  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);

  // Drag-to-resize (same pattern as GlobalNotesPanel): grab the left edge.
  const [sheetWidth, setSheetWidth] = useState(46); // vw
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(32, Math.min(90, ((window.innerWidth - e.clientX) / window.innerWidth) * 100));
      setSheetWidth(newWidth);
    };
    const handleMouseUp = () => setIsResizing(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing]);

  // Seed selection when the sheet opens.
  useEffect(() => {
    if (!open) return;
    if (initialClientId && manageable.some((c) => c.id === initialClientId)) {
      setSelectedOrgIds([initialClientId]);
    } else if (manageable.length === 1) {
      setSelectedOrgIds([manageable[0].id]);
    } else {
      setSelectedOrgIds((prev) => {
        const kept = prev.filter((id) => manageable.some((c) => c.id === id));
        return kept;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialClientId]);

  // Members/transfer panel uses the first checked org as the "manage" focus.
  const manageClientId = selectedOrgIds[0] ?? (manageable.length === 1 ? manageable[0].id : null);
  const selected = manageable.find((c) => c.id === manageClientId) ?? null;
  const inviteOrgLabels = selectedOrgIds
    .map((id) => manageable.find((c) => c.id === id)?.client_alias)
    .filter((x): x is string => !!x);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="overflow-y-auto sm:max-w-none"
        style={{ width: `clamp(320px, ${sheetWidth}vw, 100vw)` }}
      >
        {/* Drag handle — resize like the Notes panel */}
        <div
          className="absolute left-0 top-0 z-10 h-full w-1.5 cursor-ew-resize transition-colors hover:bg-primary/50"
          onMouseDown={(e) => { e.preventDefault(); setIsResizing(true); }}
          title="Drag to resize"
        />
        <SheetHeader className="text-left">
          <SheetTitle>Team &amp; Access</SheetTitle>
          <SheetDescription>
            Invite collaborators and control which practices and ACOs they can see.
          </SheetDescription>
        </SheetHeader>

        {manageable.length === 0 ? (
          <p className="mt-6 text-sm text-muted-foreground">
            You are not an owner or manager of any organization, so there is no team to manage.
          </p>
        ) : (
          <div className="mt-4 space-y-5">
            {manageable.length > 1 ? (
              <OrgMultiSelectDropdown
                orgs={manageable}
                selectedIds={selectedOrgIds}
                onChange={setSelectedOrgIds}
                roleLabel={(c) =>
                  `You are ${isAdmin && !c.user_role ? 'admin' : (c.user_role ?? 'admin')}`
                }
              />
            ) : null}

            {selectedOrgIds.length === 0 && manageable.length > 1 ? (
              <p className="text-sm text-muted-foreground">
                Select one or more organizations, then invite a collaborator or manage that team.
              </p>
            ) : selected ? (
              <>
                {selectedOrgIds.length > 1 && (
                  <p className="text-xs text-muted-foreground">
                    Invite will go to all {selectedOrgIds.length} selected organizations.
                    Member list below is for <span className="font-medium text-foreground">{selected.client_alias}</span>
                    {' '}(first selected).
                  </p>
                )}
                <TeamManagement
                  key={selected.id}
                  clientId={selected.id}
                  isOwner={selected.user_role === 'owner' || isAdmin}
                  inviteClientIds={manageable.length === 1 ? [selected.id] : selectedOrgIds}
                  inviteOrgLabels={manageable.length === 1 ? [selected.client_alias] : inviteOrgLabels}
                />
              </>
            ) : null}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
